"use client";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Code,
  Smartphone,
  Zap,
  Search,
  Layers,
  Globe,
} from "lucide-react";

const Services = () => {
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const checkScreen = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const services = [
    {
      icon: Code,
      title: "Software Empresarial",
      description:
        "Sistemas a medida que automatizan procesos críticos y escalan con tu crecimiento. Desde CRM hasta ERP completos.",
      features: ["API Integrations", "Cloud Native", "Escalabilidad"]
    },
    {
      icon: Smartphone,
      title: "Apps Móviles Nativas",
      description:
        "Aplicaciones iOS y Android que conectan tu negocio con clientes. Performance superior y experiencia fluida.",
      features: ["React Native", "UI/UX Optimizado", "Offline Support"]
    },
    {
      icon: Globe,
      title: "Plataformas Web",
      description:
        "Sitios corporativos y aplicaciones web que convierten visitantes en clientes. Rápidas, seguras y optimizadas.",
      features: ["SEO Optimizado", "Responsive Design", "High Performance"]
    },
    {
      icon: Zap,
      title: "Automatización Inteligente",
      description:
        "Eliminamos tareas repetitivas con workflows automatizados. Integración de sistemas y procesos sin fricción.",
      features: ["Workflow Automation", "Integration APIs", "Business Intelligence"]
    },
    {
      icon: Search,
      title: "Estrategia Digital",
      description:
        "Posicionamiento web y marketing digital que genera leads calificados. Estrategias basadas en datos.",
      features: ["SEO Técnico", "Growth Hacking", "Analytics Avanzados"]
    },
    {
      icon: Layers,
      title: "Arquitectura & DevOps",
      description:
        "Infraestructura cloud robusta y procesos de desarrollo ágiles. Deployments seguros y escalables.",
      features: ["Cloud Architecture", "CI/CD Pipelines", "Monitoring 24/7"]
    },
  ];

  // Motion variants
  const cardVariants = {
    hidden: { opacity: 0, y: isDesktop ? 30 : 0 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="servicios" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            Industria de <span className="bg-gradient-to-r from-[#77c593] to-[#5a9d73] bg-clip-text text-transparent">Software</span>
          </h2>
          <div className="w-32 h-1.5 bg-gradient-to-r from-[#77c593] to-[#5a9d73] mx-auto mb-6 rounded-full"></div>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Desarrollamos tecnología que transforma empresas. Desde aplicaciones web hasta sistemas complejos, 
            cada solución está diseñada para escalar y generar resultados medibles.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
              transition={{
                delay: index * 0.1,
                duration: 0.5,
                ease: "easeOut",
              }}
              className={`bg-white p-8 rounded-2xl border border-gray-200 transition-all duration-500 group relative overflow-hidden ${
                isDesktop ? "hover:border-[#77c593] hover:shadow-2xl hover:-translate-y-2" : ""
              }`}
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#77c593]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="bg-gradient-to-br from-[#77c593]/10 to-[#77c593]/5 p-4 rounded-2xl w-fit mb-6 group-hover:from-[#77c593] group-hover:to-[#5a9d73] transition-all duration-500">
                  <service.icon
                    size={32}
                    className="text-[#77c593] group-hover:text-white transition-colors duration-500"
                  />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#77c593] transition-colors duration-300">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                {/* Features tags */}
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature, featureIndex) => (
                    <span
                      key={featureIndex}
                      className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full group-hover:bg-[#77c593]/10 group-hover:text-[#77c593] transition-all duration-300"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
